// Shared in-game settings modal used by every game (Ludo / X&O /
// Rummy). Originally lived inside `games/rummy/SettingsMenu.tsx`
// — promoted here so all games share one settings UX: sound toggle,
// optional voice toggles (mic + listen), and an Exit Game row.
//
// Each game's Page.tsx owns its own toggle state + WS voice handles
// and passes them in via the callbacks. Voice rows are hidden when
// `voiceEnabled=false` (Ludo-no-voice matches, Rummy in lobby phase,
// etc.) instead of showing disabled rows.
//
// CSS lives in `core.css` — the `.game-settings-*` classes so any
// game can use this component without importing another game's
// stylesheet.

import Icon from './Icon';

type Props = {
  open: boolean;
  soundOn: boolean;
  micMuted: boolean;
  listenMuted: boolean;
  voiceEnabled: boolean;
  onToggleSound:  () => void;
  onToggleMic:    () => void;
  onToggleListen: () => void;
  onExit:  () => void;
  onClose: () => void;
  /** Optional label override for the "Exit Game" row — e.g. in the
      lobby phase we want "Exit to Home" instead. */
  exitLabel?: string;
  /** Which edge of the top bar the caller's settings button lives on.
      The dropdown panel slides down from the same side so it hangs
      under the button instead of floating in the opposite corner.
      Defaults to 'right' (Rummy's layout). */
  anchor?: 'left' | 'right';
};

export default function GameSettingsMenu(p: Props) {
  if (!p.open) return null;
  const anchor = p.anchor || 'right';
  return (
    <div
      className={`game-settings-overlay game-settings-overlay--${anchor}`}
      onClick={p.onClose}
      role="dialog"
      aria-label="Game settings"
    >
      <div
        className="game-settings-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="game-settings-close"
          onClick={p.onClose}
          aria-label="Close settings"
        >
          <Icon name="close" size={18} />
        </button>

        <div className="game-settings-title">SETTINGS</div>

        <button
          type="button"
          className="game-settings-row"
          onClick={p.onToggleSound}
        >
          <Icon name={p.soundOn ? 'volume_up' : 'volume_off'} size={20} />
          <span className="flex-1 text-left">Sound</span>
          <span className={p.soundOn ? 'text-[#dfff7a]' : 'text-neutral-400'}>
            {p.soundOn ? 'On' : 'Off'}
          </span>
        </button>

        {p.voiceEnabled && (
          <>
            <button
              type="button"
              className="game-settings-row"
              onClick={p.onToggleMic}
            >
              <Icon name={p.micMuted ? 'mic_off' : 'mic'} size={20} />
              <span className="flex-1 text-left">My mic</span>
              <span className={p.micMuted ? 'text-neutral-400' : 'text-[#dfff7a]'}>
                {p.micMuted ? 'Off' : 'On'}
              </span>
            </button>
            <button
              type="button"
              className="game-settings-row"
              onClick={p.onToggleListen}
            >
              <Icon name={p.listenMuted ? 'headphones_disabled' : 'headphones'} size={20} />
              <span className="flex-1 text-left">Hear others</span>
              <span className={p.listenMuted ? 'text-neutral-400' : 'text-[#dfff7a]'}>
                {p.listenMuted ? 'Off' : 'On'}
              </span>
            </button>
          </>
        )}

        <button
          type="button"
          className="game-settings-row game-settings-row--danger"
          onClick={p.onExit}
        >
          <Icon name="logout" size={20} />
          <span className="flex-1 text-left">{p.exitLabel || 'Exit Game'}</span>
        </button>
      </div>
    </div>
  );
}
